import Immutable from 'immutable';
import _ from 'lodash';
import FinesConstants from 'app/constants/FinesConstants';
import BorrowingConstants from 'app/constants/BorrowingConstants';

const {
  FETCH_FINES_REQUEST,
  FETCH_FINES_SUCCESS,
  FETCH_FINES_FAILURE,
  FETCH_FINES_PAYMENT_FORM_SUCCESS,
  FETCH_FINES_PAYMENT_FORM_FAILURE,
  UPDATE_PAGE_REQUEST,
  UPDATE_PAGE_SUCCESS,
  UPDATE_PAGE_FAILURE,
  CREATE_GLOBAL_PAYMENTS_ORDER_REQUEST,
  CREATE_GLOBAL_PAYMENTS_ORDER_SUCCESS,
  CREATE_GLOBAL_PAYMENTS_ORDER_FAILURE,
  CREATE_ORDER_REQUEST,
  CREATE_ORDER_SUCCESS,
  CREATE_ORDER_FAILURE,
  CREATE_PAYMENT_REQUEST,
  CREATE_PAYMENT_SUCCESS,
  CREATE_PAYMENT_FAILURE,
  CLEAR_FINES_TRANSACTION
} = FinesConstants;

const initialState = Immutable.fromJS({
  globalPayments: {
    isFetchingOrder: false,
    order: null
  },
  results: [],
  isFetching: false,
  order: null,
  payment: null
});

/**
 * CORE-78885: Immutable 4 merges two Lists by concatenating them - `ListPrototype.merge` is
 * literally `ListPrototype.concat` - so `mergeDeep`-ing a fines payload APPENDED the incoming
 * `results` ids to the ones already in the store instead of replacing them. Any re-fetch of the
 * fees list, such as the "Update total" button, therefore rendered every fee one extra time
 * (the total was unaffected because it comes from the summary, not from `results`).
 *
 * Merge the rest of the payload as before, but always replace the id list outright - the same
 * thing HoldsReducer and CheckedOutReducer already do with their `items`.
 */
function mergeFines(state, finesPayload) {
  const fines = Immutable.fromJS(finesPayload || {});
  const merged = state.mergeDeep(fines);

  return fines.has('results') ? merged.set('results', fines.get('results')) : merged;
}

export default function finesReducer(state = initialState, action) {
  switch (action.type) {
    case CREATE_GLOBAL_PAYMENTS_ORDER_REQUEST: {
      return state.setIn(['globalPayments', 'isFetchingOrder'], true).setIn(['globalPayments', 'order'], null);
    }

    case CREATE_GLOBAL_PAYMENTS_ORDER_FAILURE: {
      return state.setIn(['globalPayments', 'isFetchingOrder'], false).setIn(['globalPayments', 'order'], null);
    }

    case CREATE_GLOBAL_PAYMENTS_ORDER_SUCCESS: {
      return state
        .setIn(['globalPayments', 'isFetchingOrder'], false)
        .setIn(
          ['globalPayments', 'order'],
          Immutable.fromJS(action.payload?.borrowing?.fines?.globalPaymentsOrder ?? {})
        );
    }

    case FETCH_FINES_SUCCESS: {
      return mergeFines(state, action.payload.borrowing.fines).set('isFetching', false);
    }

    case FETCH_FINES_PAYMENT_FORM_SUCCESS:
    case FETCH_FINES_PAYMENT_FORM_FAILURE: {
      return state.mergeDeep(Immutable.fromJS(action.payload.borrowing)).set('isFetching', false);
    }

    case FETCH_FINES_REQUEST:
    case UPDATE_PAGE_REQUEST: {
      return state.set('isFetching', true);
    }

    // CORE-78885: this read `payload.borrowing`, one level above the fines data, so the
    // response to the "Update total" button was written to a `fines.fines` key that nothing
    // reads - the refreshed results and pagination never reached the page at all.
    case UPDATE_PAGE_SUCCESS: {
      return mergeFines(state, action.payload.borrowing.fines).set('isFetching', false);
    }

    case FETCH_FINES_FAILURE:
    case UPDATE_PAGE_FAILURE: {
      return state.set('isFetching', false);
    }

    case BorrowingConstants.FETCH_SUMMARIES_SUCCESS: {
      return state.mergeDeep(Immutable.fromJS(_.get(action, 'payload.borrowing', {})));
    }

    // Paypal Order
    case CREATE_ORDER_FAILURE: {
      return state.merge({
        order: Immutable.fromJS({ settingUpTransaction: false })
      });
    }

    case CREATE_ORDER_REQUEST: {
      return state.merge({
        order: Immutable.fromJS({ settingUpTransaction: true })
      });
    }

    case CREATE_ORDER_SUCCESS: {
      return state.merge({
        order: Immutable.fromJS({ ...action.payload.borrowing.fines.order, settingUpTransaction: false })
      });
    }

    // Paypal Payment
    case CREATE_PAYMENT_FAILURE: {
      return state.merge({
        payment: Immutable.fromJS({ approvingTransaction: false })
      });
    }

    case CREATE_PAYMENT_REQUEST: {
      return state.merge({
        payment: Immutable.fromJS({ approvingTransaction: true })
      });
    }

    case CREATE_PAYMENT_SUCCESS: {
      return state.merge({
        payment: Immutable.fromJS({ ...action.payload.borrowing.fines.payment, approvingTransaction: false })
      });
    }

    case CLEAR_FINES_TRANSACTION: {
      return state.withMutations(newState => {
        newState.delete('order');
        newState.delete('payment');
      });
    }

    default:
      return state;
  }
}
